import { type FC } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ProductTypeType } from "@/types/graphql/partner.generated";

interface ProductTypeOption {
  value: ProductTypeType;
  label: string;
}

interface ProductTypeFieldSingleProps {
  multiple?: false;
  value: ProductTypeType | null;
  onChange: (value: ProductTypeType | null) => void;
  label?: string;
  variant?: "outlined" | "standard" | "filled";
}

interface ProductTypeFieldMultipleProps {
  multiple: true;
  value: ProductTypeType[];
  onChange: (value: ProductTypeType[]) => void;
  label?: string;
  variant?: "outlined" | "standard" | "filled";
}

type ProductTypeFieldProps =
  | ProductTypeFieldSingleProps
  | ProductTypeFieldMultipleProps;

/**
 * Селект типа продукта (референс: components/ProductTypeField). У нас
 * ProductType = PPV | SUBSCRIPTION (без DONATION референса — этого типа
 * нет в backend-схеме Task 3).
 */
export const ProductTypeField: FC<ProductTypeFieldProps> = ({
  multiple,
  value,
  onChange,
  label,
  variant,
}) => {
  const { t } = useTranslation();

  const options: ProductTypeOption[] = [
    { value: ProductTypeType.PpvType, label: t("products.type.ppv") },
    {
      value: ProductTypeType.SubscriptionType,
      label: t("products.type.subscription"),
    },
  ];

  if (multiple) {
    const selected = options.filter((option) => value.includes(option.value));

    return (
      <Autocomplete
        multiple
        fullWidth
        filterSelectedOptions
        options={options}
        value={selected}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, current) =>
          option.value === current.value
        }
        onChange={(_event, newValue) =>
          onChange(newValue.map((option) => option.value))
        }
        renderInput={(params) => (
          <TextField {...params} variant={variant} label={label} />
        )}
      />
    );
  }

  const selected = options.find((option) => option.value === value) ?? null;

  return (
    <Autocomplete
      fullWidth
      options={options}
      value={selected}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.value === current.value}
      onChange={(_event, newValue) => onChange(newValue?.value ?? null)}
      renderInput={(params) => (
        <TextField {...params} variant={variant} label={label} />
      )}
    />
  );
};
